import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Cloud, CloudRain, CloudLightning, Snowflake, Wind, Droplets, Thermometer, Calendar, Info, ChevronRight } from 'lucide-react';

const WeatherDetail = () => {
    const navigate = useNavigate();
    const [currentWeather, setCurrentWeather] = useState(null);
    const [forecast, setForecast] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDetailedWeather();
    }, []);

    const fetchDetailedWeather = async () => {
        try {
            if ('geolocation' in navigator) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        await getWeatherData(latitude, longitude);
                    },
                    (err) => {
                        console.log('Using weather fallback (GPS not available or timeout)');
                        getWeatherData(48.1351, 11.5820);
                    },
                    { timeout: 5000, enableHighAccuracy: false }
                );
            } else {
                getWeatherData(48.1351, 11.5820);
            }
        } catch (err) {
            setError('Fehler beim Abrufen des Standorts.');
            setLoading(false);
        }
    };

    const getWeatherData = async (lat, lon) => {
        try {
            // Use our backend proxy to avoid CORS issues and stabilize requests
            const url = `/api/weather/current/?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,uv_index_max&timezone=auto`;
            
            const response = await fetch(url);
            if (!response.ok) throw new Error('Wetter-API Fehler');
            
            const data = await response.json();
            
            setCurrentWeather(data.current);
            
            // Process 7-day forecast
            const daily = data.daily;
            const forecastData = daily.time.map((time, i) => ({
                date: new Date(time).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }),
                tempMax: Math.round(daily.temperature_2m_max[i]),
                tempMin: Math.round(daily.temperature_2m_min[i]),
                weatherCode: daily.weather_code[i],
                precip: daily.precipitation_sum[i],
                uv: daily.uv_index_max[i]
            }));
            setForecast(forecastData);
            setLoading(false);
        } catch (err) {
            setError('Wetterdaten konnten nicht geladen werden.');
            setLoading(false);
        }
    };

    const getWeatherIcon = (code, isLarge = false) => {
        const size = isLarge ? 48 : 24;
        const className = isLarge ? "text-plantlix-500" : "text-gray-600 dark:text-gray-300";
        
        if (code === 0 || code === 1) return <Sun size={size} className="text-yellow-500" />;
        if (code === 2 || code === 3) return <Cloud size={size} className={className} />;
        if (code >= 45 && code <= 48) return <Wind size={size} className={className} />;
        if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return <CloudRain size={size} className="text-blue-500" />;
        if (code >= 71 && code <= 77) return <Snowflake size={size} className="text-blue-300" />;
        if (code >= 95 && code <= 99) return <CloudLightning size={size} className="text-purple-500" />;
        return <Cloud size={size} className={className} />;
    };

    const getWeatherDescription = (code) => {
        if (code === 0) return 'Klarer Himmel';
        if (code === 1) return 'Hauptsächlich klar';
        if (code === 2) return 'Teilweise bewölkt';
        if (code === 3) return 'Bedeckt';
        if (code >= 45 && code <= 48) return 'Nebel';
        if (code >= 51 && code <= 55) return 'Nieselregen';
        if (code >= 61 && code <= 65) return 'Regen';
        if (code >= 71 && code <= 77) return 'Schneefall';
        if (code >= 80 && code <= 82) return 'Regenschauer';
        if (code >= 95 && code <= 99) return 'Gewitter';
        return 'Bewölkt';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-plantlix-500 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] pb-12">
            {/* Header */}
            <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 shadow-sm">
                <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                        <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        Wettervorhersage
                    </h1>
                    <div className="w-10"></div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 mt-8 space-y-8">
                {/* Current Weather Card */}
                {currentWeather && (
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800 p-8">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                            <div className="text-center md:text-left">
                                <span className="text-sm font-bold text-plantlix-500 uppercase tracking-wider">Aktuell</span>
                                <h2 className="text-6xl font-black text-gray-900 dark:text-white mt-2">
                                    {Math.round(currentWeather.temperature_2m)}°C
                                </h2>
                                <p className="text-xl text-gray-500 dark:text-gray-400 mt-2">
                                    {getWeatherDescription(currentWeather.weather_code)}
                                </p>
                            </div>
                            
                            <div className="relative">
                                <div className="absolute inset-0 bg-yellow-400/20 blur-3xl rounded-full"></div>
                                {getWeatherIcon(currentWeather.weather_code, true)}
                            </div>

                            <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-2xl flex items-center gap-3">
                                    <Droplets className="text-blue-500" />
                                    <div>
                                        <div className="text-xs text-blue-600 dark:text-blue-400 font-bold uppercase">Feuchte</div>
                                        <div className="font-bold dark:text-blue-100">{currentWeather.relative_humidity_2m}%</div>
                                    </div>
                                </div>
                                <div className="bg-plantlix-50 dark:bg-plantlix-900/20 p-4 rounded-2xl flex items-center gap-3">
                                    <Wind className="text-plantlix-600" />
                                    <div>
                                        <div className="text-xs text-plantlix-700 dark:text-plantlix-400 font-bold uppercase">Wind</div>
                                        <div className="font-bold dark:text-plantlix-100">{currentWeather.wind_speed_10m} km/h</div>
                                    </div>
                                </div>
                                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-2xl flex items-center gap-3">
                                    <Thermometer className="text-orange-500" />
                                    <div>
                                        <div className="text-xs text-orange-600 dark:text-orange-400 font-bold uppercase">Gefühlt</div>
                                        <div className="font-bold dark:text-orange-100">{Math.round(currentWeather.apparent_temperature)}°C</div>
                                    </div>
                                </div>
                                <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-2xl flex items-center gap-3">
                                    <CloudRain className="text-cyan-500" />
                                    <div>
                                        <div className="text-xs text-cyan-600 dark:text-cyan-400 font-bold uppercase">Regen</div>
                                        <div className="font-bold dark:text-cyan-100">{currentWeather.precipitation || 0} mm</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 7-Day Forecast */}
                <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2 px-2">
                        <Calendar className="w-5 h-5 text-plantlix-500" />
                        7-Tage-Vorhersage
                    </h3>
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 overflow-hidden">
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {forecast.map((day, i) => (
                                <button
                                    key={i}
                                    onClick={() => navigate(`/weather/day/${i}`)}
                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group text-left"
                                >
                                    <div className="w-24 text-sm font-bold text-gray-700 dark:text-gray-300">
                                        {i === 0 ? 'Heute' : day.date}
                                    </div>
                                    <div className="flex-1 flex items-center gap-3">
                                        {getWeatherIcon(day.weatherCode)}
                                        <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
                                            {getWeatherDescription(day.weatherCode)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-bold text-gray-900 dark:text-white">{day.tempMax}°</span>
                                        <span className="text-sm text-gray-400 dark:text-gray-500">{day.tempMin}°</span>
                                        <div className="w-12 text-right">
                                            {day.precip > 0 ? (
                                                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">
                                                    {day.precip}mm
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-gray-300">0mm</span>
                                            )}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-plantlix-500 group-hover:translate-x-0.5 transition-all" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Attribution & License */}
                <div className="bg-gray-100 dark:bg-[#1e1e1e] p-6 rounded-2xl border border-gray-200 dark:border-gray-800">
                    <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div className="space-y-2">
                            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-tighter">Datenquelle & Rechtliches</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                                Wetterdaten bereitgestellt durch <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" className="text-plantlix-500 hover:underline font-bold">Open-Meteo</a>. 
                                Die Nutzung erfolgt unter der <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className="text-plantlix-500 hover:underline">Creative Commons Attribution 4.0 International Lizenz</a>.
                            </p>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                                Plantlix übernimmt keine Gewähr für die Richtigkeit der Wetterdaten. Die Standortbestimmung erfolgt lokal im Browser.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WeatherDetail;
